<script lang="ts">
	import { Toast, Toaster } from "toaster"
	import { Overlay, OverlayController } from "overlay"

	import { NavigationStore } from "../../stores/NavigationContext"
	import Sidebar from "../nav/Sidebar.svelte"
	import { ROUTES } from "./Routes"
	import { closeCtxMenu, ContextMenu } from "context-menu"
	import { onMount } from "svelte"
	import { LatestVersion, ReleaseNotes } from "../../release-notes"
	import ShowLibrary from "./show-library/ShowLibrary.svelte"
	import MediaLibrary from "./media-library/MediaLibrary.svelte"
	import Schedule from "./schedule/Schedule.svelte"
	import { Tooltip } from "tooltip"
	import { openWhatsNewOverlay } from "../reusable/overlays/whats-new"
	import { openCourtPortalInviteOverlay } from "../reusable/overlays/court-portal-invite"
	import { UserStore } from "../../stores/UserStore.svelte"
	import { openProContentSolutions } from "luxedo-comps"
	import { COURT_PORTAL_PRIV, COURT_PORTAL_URL, PRO_PRIV } from "../../types/consts"
	import { ScheduleController } from "./schedule/ScheduleController"
	import { DataHandlerDevice, DataHandlerScene } from "luxedo-data"
	import ProjectorMenu from "./projectors/DeviceMenu.svelte"
	import PageNavigator from "../nav/PageNavigator.svelte"
	import TagColorPicker from "../reusable/tags/tag-color-picker/TagColorPicker.svelte"
	import LuxAi from "./lux-ai/LuxAI.svelte"

	let activeRoute: keyof typeof ROUTES = $state()

	NavigationStore.subscribe((route) => (activeRoute = route))

	// A context menu's full-screen backdrop shares the overlay stacking layer, so when a
	// menu option opens an overlay (e.g. a delete confirmation) the still-open menu would
	// otherwise swallow the first click meant for that overlay. Dismiss the menu whenever a
	// new overlay is mounted. Guarded on an increasing count so menus opened *inside* an
	// existing overlay stay usable.
	let openOverlayCount = 0
	OverlayController.subscribe((overlays) => {
		const count = Object.keys(overlays).length
		if (count > openOverlayCount) closeCtxMenu()
		openOverlayCount = count
	})

	function showWhatsNew() {
		const KEY = "last-visit-version"
		const visitedVersion = localStorage.getItem(KEY)

		if (!visitedVersion || visitedVersion != LatestVersion) {
			openWhatsNewOverlay()
			localStorage.setItem(KEY, LatestVersion)
		}
	}

	/** One-time invite to the new court-portal app for users holding the rollout
	 * priv. Returns true when the overlay was shown (so What's New can yield —
	 * never stack two announcement modals on one login). */
	function showCourtPortalInvite(): boolean {
		const KEY = "court-portal-invite-seen"
		if (!COURT_PORTAL_URL || localStorage.getItem(KEY)) return false

		openCourtPortalInviteOverlay()
		localStorage.setItem(KEY, "1")
		return true
	}

	/** One-time nudge for PRO accounts toward the custom-show order form. Returns
	 * true when the overlay was shown, so What's New yields to it. */
	function showProContentPrompt(): boolean {
		const KEY = "pro-content-solutions-seen"
		if (localStorage.getItem(KEY)) return false

		openProContentSolutions()
		localStorage.setItem(KEY, "1")
		return true
	}

	onMount(() => {
		if (!(activeRoute in ROUTES)) NavigationStore.set("show-library")

		console.log({ ScheduleController, DataHandlerDevice, DataHandlerScene })
		console.warn(`VERSION ${LatestVersion} LOADED`)

		// The court-portal invite and PRO content prompt are priv-gated, so wait for
		// the user to load before choosing which (single) announcement overlay to show.
		let announced = false
		const unsubscribe = UserStore.subscribe((user) => {
			if (announced || !user) return
			announced = true
			setTimeout(() => {
				const invited = user.hasPriv(COURT_PORTAL_PRIV) && showCourtPortalInvite()
				const prompted = !invited && user.hasPriv(PRO_PRIV) && showProContentPrompt()
				if (!invited && !prompted) showWhatsNew()
			})
		})
		return unsubscribe
	})
</script>

<!-- chargebee checkout script -->
<svelte:head>
	<script
		src="https://js.chargebee.com/v2/chargebee.js"
		data-cb-site={import.meta.env.VITE_CHARGEBEE_SITE}
	></script>
</svelte:head>

<PageNavigator>
	{#if activeRoute === "projector"}
		<ProjectorMenu />
	{:else if activeRoute === "show-library"}
		<ShowLibrary />
	{:else if activeRoute === "media-library"}
		<MediaLibrary />
	{:else if activeRoute === "schedule"}
		<Schedule />
	{:else if activeRoute === "lux-ai"}
		<LuxAi />
	{:else}
		<h1>Page not found</h1>
	{/if}
</PageNavigator>

<TagColorPicker />
<Tooltip />
<Toaster topMargin={"1rem"} errorBackground="var(--red)" errorColor="var(--color-text-light)" />

<Overlay />
<ContextMenu />

<style>
</style>
